import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import Button from '@clayui/button';
import ClayLayout from '@clayui/layout';
import { useApiMutation } from '../../../apis/useApi';
import FormComponent from '../../../generic-components/formComponent/FormComponent';
import Loader from '../../../generic-components/loader/Loader';
import { RootState } from '../../../store';
import IndustriesSelect from '../../IndustriesSelect/IndustriesSelect';
import {
    attractionBusinessDetailsFields,
    BusinessDetailsFields
} from '../../../utils/Config';
import '../AttractionBasicDetail/AttractionBasicDetail.css';
import { useFetchListTypeEntries } from '../../../services/ListTypeEntryService';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { updateCommonValues } from '../../../slices/commonSlice';
import { updateAttractionBusinessDetailValues } from '../../../slices/attractionBusinessDetailSlice';
import { expansionInquiryService } from '../../../services/expansionInquiryService';
import { serializePayload } from '../../../utils/commonUtils';

const AttractionBusinessDetail: React.FC<{
    setStep: React.Dispatch<React.SetStateAction<number>>;
    setInquiry: React.Dispatch<React.SetStateAction<number>>;
    setResStatusMessage: React.Dispatch<React.SetStateAction<string>>;
    onLoadingChange?: (loading: boolean) => void;
}> = ({ setStep, setInquiry, setResStatusMessage, onLoadingChange }) => {
    const dispatch = useAppDispatch();

    // Get data from slices
    const attractionBasicDetail = useAppSelector(
        (state) => state.attractionBasicDetail
    );
    const common = useAppSelector((state) => state.common);
    const attractionBusinessDetail = useAppSelector(
        (state) => state.attractionBusinessDetail
    );

    const {
        control,
        register,
        formState: { errors: formErrors, touchedFields },
        watch,
        handleSubmit,
        setError,
        clearErrors,
        getValues,
        setValue
    } = useForm({ mode: 'all' });

    const [dropdownOptions, setDropdownOptions] = useState<any>({});
    const [industryOptions, setIndustryOptions] = useState<any[]>([]);
    const [subIndustryOptions, setSubIndustryOptions] = useState<any[]>([]);

    const ANNUAL_REVENUE = useFetchListTypeEntries('ANNUAL_REVENUE');
    const NUMBER_OF_EMPLOYEES = useFetchListTypeEntries('NUMBER_OF_EMPLOYEES');
    const ATTRACTION_BUSINESS_PURPOSE = useFetchListTypeEntries(
        'ATTRACTION_BUSINESS_PURPOSE'
    );
    const COUNTRY_LOCATION = useFetchListTypeEntries('COUNTRY_LOCATION');

    useEffect(() => {
        const updateDropdownOptions = async () => {
            const annualRevenue = ANNUAL_REVENUE;
            const numberOfEmployees = NUMBER_OF_EMPLOYEES;
            const businessPurpose = ATTRACTION_BUSINESS_PURPOSE;
            const location = COUNTRY_LOCATION;
            setDropdownOptions((prevData: any) => ({
                ...prevData,
                annualRevenue,
                numberOfEmployees,
                businessPurpose,
                location
            }));
        };

        updateDropdownOptions();
    }, [
        ANNUAL_REVENUE,
        NUMBER_OF_EMPLOYEES,
        ATTRACTION_BUSINESS_PURPOSE,
        COUNTRY_LOCATION
    ]);

    const managedFields = React.useMemo(
        () => [
            ...attractionBusinessDetailsFields.map((f) => f.name),
            'industrySubcategory'
        ],
        []
    );

    const getManagedValues = (currentValues: any) => {
        const payload: any = {};
        managedFields.forEach((name) => {
            if (currentValues[name] !== undefined) {
                payload[name] = currentValues[name];
            }
        });
        return payload;
    };

    useEffect(() => {
        // Set form values from Redux on mount
        if (common.businessEmail)
            setValue('businessEmail', common.businessEmail);
        if (attractionBusinessDetail.location)
            setValue('location', attractionBusinessDetail.location);
        if (attractionBusinessDetail.numberOfEmployees)
            setValue(
                'numberOfEmployees',
                attractionBusinessDetail.numberOfEmployees
            );
        if (attractionBusinessDetail.annualRevenue)
            setValue('annualRevenue', attractionBusinessDetail.annualRevenue);
        if (attractionBusinessDetail.businessPurpose)
            setValue(
                'businessPurpose',
                attractionBusinessDetail.businessPurpose
            );
    }, [common, attractionBusinessDetail, setValue]);

    useEffect(() => {
        return () => {
            const values = getManagedValues(getValues());
            dispatch(updateCommonValues(values));
            dispatch(updateAttractionBusinessDetailValues(values));
        };
    }, [dispatch, getValues, managedFields]);

    const submitDataHandler = useApiMutation<any, {}>(
        'o/c/attractioninquiries/scopes/lead%20management',
        'post',
        {
            onSuccess: async (data) => {
                if (data && data?.id) {
                    // Success - redirect to final screen with success
                    setStep(3);
                    setInquiry(data.id);
                    setResStatusMessage(
                        'Your inquiry has been submitted successfully!'
                    );
                    toast.success(
                        'Your inquiry has been submitted successfully!',
                        {
                            theme: 'light',
                            autoClose: 2000
                        }
                    );
                } else {
                    setStep(3);
                    setInquiry(0);
                    setResStatusMessage(
                        data?.status?.statusMessage || 'Something went wrong'
                    );
                    toast.error(
                        data?.status?.statusMessage || 'Something went wrong',
                        {
                            theme: 'light',
                            autoClose: 4000
                        }
                    );
                }
                setTimeout(() => {
                    window.scrollTo(0, 0);
                }, 1000);
                onLoadingChange?.(false);
            },
            onError: (error: any) => {
                setStep(3);
                setInquiry(0);
                toast.error('Submission failed. Please try again.', {
                    theme: 'light',
                    autoClose: 4000
                });
                onLoadingChange?.(false);
            }
        }
    );

    // Function to check if email already exists using service
    const checkEmailExistsHandler = async (contactEmail: string) => {
        try {
            const response =
                await expansionInquiryService.checkAttractionFormSubmissionExists(
                    contactEmail
                );

            if (response?.data?.exists) {
                // Email already exists - redirect to final screen with error
                setStep(3);
                setInquiry(0);
                setResStatusMessage(
                    'You have already submitted an inquiry with this email address'
                );
                toast.error(
                    'You have already submitted an inquiry with this email address',
                    {
                        theme: 'light',
                        autoClose: 4000
                    }
                );
                onLoadingChange?.(false);
            } else {
                // Email doesn't exist - proceed with submission
                proceedWithSubmission();
            }
        } catch (error: any) {
            console.error('Error checking email:', error);
            setStep(3);
            setInquiry(0);
            setResStatusMessage('Error checking email existence');
            toast.error('Error checking email existence', {
                theme: 'light',
                autoClose: 4000
            });
            onLoadingChange?.(false);
        }
    };

    const proceedWithSubmission = async () => {
        try {
            // Get current form values
            const data = getValues();

            // isSME calculation logic
            const annualRevenue = data.annualRevenue;
            const numberOfEmployees = data.numberOfEmployees;
            const isSME =
                (annualRevenue !== '270MillionUSDto500MillionUSD' ||
                    annualRevenue !== '501MillionUSDto999MillionUSD' ||
                    annualRevenue !== 'moreThan1BillionUSD') &&
                numberOfEmployees !== 'moreThan500';

            // Format industry options for payload
            const industryNames = (
                Array.isArray(data.industry) ? data.industry : [data.industry]
            )
                .map(
                    (id: any) =>
                        industryOptions.find(
                            (opt) => opt.key.toString() === id.toString()
                        )?.name
                )
                .filter(Boolean)
                .join(', ');

            const subIndustryNames = (
                Array.isArray(data.industrySubcategory)
                    ? data.industrySubcategory
                    : [data.industrySubcategory]
            )
                .map(
                    (id: any) =>
                        subIndustryOptions.find(
                            (opt) => opt.key.toString() === id.toString()
                        )?.name
                )
                .filter(Boolean)
                .join(', ');

            // Combine data from slices with current form data
            let payload: any = {
                businessEmail: common.businessEmail,
                website: attractionBasicDetail.website,
                contactName: attractionBasicDetail.contactName,
                companyName: attractionBasicDetail.companyName,
                jobTitle: attractionBasicDetail.designation,
                numberOfEmployees: data.numberOfEmployees,
                businessPurpose: data.businessPurpose,
                businessSectorsYourCompanyIsEngagedIn: industryNames,
                businessSubSector: subIndustryNames,
                location: data.location,
                annualRevenue: data.annualRevenue,
                phoneNumber: attractionBasicDetail.phoneNumber,
                isSME
            };
            const sanitizedPayload = serializePayload(payload);
            submitDataHandler.mutate({
                data: sanitizedPayload,
                params: {
                    p_auth: (window as any)?.Liferay?.authToken
                }
            });
        } catch (error) {
            console.error('Unexpected error during submission:', error);
            setStep(3);
            setInquiry(0);
            toast.error('Unexpected error occurred.', {
                theme: 'light',
                autoClose: 4000
            });
        }
    };

    const submitHandler = async (data: any) => {
        onLoadingChange?.(true);

        // First check if email already exists
        if (common.businessEmail) {
            await checkEmailExistsHandler(common.businessEmail);
        } else {
            // Handle case where businessEmail is null
            setStep(3);
            setInquiry(0);
            setResStatusMessage('Business email is required');
            toast.error('Business email is required', {
                theme: 'light',
                autoClose: 4000
            });
            onLoadingChange?.(false);
        }
    };

    const primaryButtonStyle: React.CSSProperties = {
        '--bg-cut-background': '#ffffff'
    } as React.CSSProperties;

    return (
        <>
            <form
                onSubmit={handleSubmit(submitHandler)}
                autoComplete="off"
                className="basic_detail_form"
            >
                <ClayLayout.Row justify="start">
                    {attractionBusinessDetailsFields.map((field) => {
                        if (field.name === 'industry') {
                            return (
                                <IndustriesSelect
                                    key={field.name}
                                    control={control}
                                    watch={watch}
                                    errors={formErrors}
                                    setValue={setValue}
                                    register={register}
                                    onIndustryOptionsChange={setIndustryOptions}
                                    onSubIndustryOptionsChange={
                                        setSubIndustryOptions
                                    }
                                />
                            );
                        }

                        return (
                            <ClayLayout.Col
                                key={field.name}
                                xl={6}
                                lg={6}
                                md={6}
                                sm={6}
                                xs={12}
                            >
                                <FormComponent
                                    {...field}
                                    register={register}
                                    control={control}
                                    errors={formErrors}
                                    watch={watch}
                                    setError={setError}
                                    errorTitle={field.errorMessage}
                                    options={dropdownOptions[field.name] || []}
                                />
                            </ClayLayout.Col>
                        );
                    })}
                </ClayLayout.Row>
                {/* <div className="button_container">
          <SubmitButton label="Submit" />
        </div> */}
                <div className="submit-button-container">
                    <Button
                        type="submit"
                        className="bottom-cut"
                        style={primaryButtonStyle}
                    >
                        <span className="bottom-cut-span"></span>
                        Submit
                    </Button>
                </div>
            </form>
        </>
    );
};

export default AttractionBusinessDetail;
